<script setup lang="ts">
import * as maplibregl from 'maplibre-gl'
import { storeToRefs } from 'pinia'
import { watch, inject, ref, shallowRef, onUnmounted, computed, type Ref } from 'vue'
import { useRouter } from 'vue-router'

import { useTrackResolution, type Resolution } from '@/composables/useTrackResolution'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { isTouchDevice } from '@/services/uiService'
import { useMobileBatchSelectionStore } from '@/stores/mobileBatchSelectionStore'
import {
  useSearchWorkspaceStore,
  type SelectedSearchWorkspace,
} from '@/stores/searchWorkspaceStore'
import { useUiStore } from '@/stores/uiStore'
import { hideMapHoverPopup, showMapHoverPopup } from '@/utils/mapHoverPopup'
import { findFirstSymbolLayerId } from '@/utils/mapLayerOrder'
import {
  buildWorkspaceCollectionHoverPopupHtml,
  formatWorkspacePopupDateRange,
} from '@/utils/workspaceHoverPopup'

import type {
  Map as MapLibreMap,
  GeoJSONSource,
  MapLayerMouseEvent,
  MapLayerTouchEvent,
  Point,
} from 'maplibre-gl'

defineOptions({ name: 'WorkspaceSearchLayer' })

const searchWorkspaceStore = useSearchWorkspaceStore()
const mobileBatchSelectionStore = useMobileBatchSelectionStore()
const uiStore = useUiStore()
const router = useRouter()
const { isTrackDrawingActive } = storeToRefs(uiStore)

const emit = defineEmits<{
  'workspace-context-menu': [detail: { workspaceId: string; clientX: number; clientY: number }]
}>()

// Inject map instance from parent
const mapInstance = inject<Ref<MapLibreMap | null>>('mapInstance', ref(null))
const mapLoaded = inject<Ref<boolean>>('mapLoaded', ref(false))
const mapStyleRevision = inject<Ref<number>>('mapStyleRevision', ref(0))

// ── Fixed source / layer IDs (aggregated model) ───────────────────────────────
const MAIN_SOURCE_ID = 'search-workspace-main'
const OVERLAY_SOURCE_ID = 'search-workspace-overlay'
const MAIN_OUTLINE_LAYER_ID = 'search-workspace-outline'
const MAIN_LINE_LAYER_ID = 'search-workspace-line'
const MAIN_TOUCH_LAYER_ID = 'search-workspace-touch'
const OVERLAY_OUTLINE_LAYER_ID = 'search-workspace-overlay-outline'
const OVERLAY_LINE_LAYER_ID = 'search-workspace-overlay-line'

const DEFAULT_LINE_WIDTH = 4
const HOVER_LINE_WIDTH = 6
const DEFAULT_OUTLINE_WIDTH = 8
const HOVER_OUTLINE_WIDTH = 12
const DEFAULT_OUTLINE_OPACITY = 0.8
const HOVER_OUTLINE_OPACITY = 1.0
const TOUCH_TAP_TOLERANCE_PX = 12
const TOUCH_LONG_PRESS_DELAY_MS = 500
const TOUCH_SYNTHETIC_CLICK_SUPPRESSION_MS = 750

// Track ongoing requests to prevent multiple simultaneous API calls for the same workspace
const ongoingRequests = ref<Set<string>>(new Set())

// Hover popup
const isTouchEnvironment = isTouchDevice()
const hoverPopup = shallowRef<maplibregl.Popup | null>(null)
let hidePopupTimer: ReturnType<typeof setTimeout> | null = null

const showWorkspacePopup = (lngLat: maplibregl.LngLat, workspaceId: string) => {
  const map = mapInstance.value
  if (!map) return

  if (hidePopupTimer !== null) {
    clearTimeout(hidePopupTimer)
    hidePopupTimer = null
  }

  const workspace = searchWorkspaceStore.allWorkspaces.find((w) => w.clientId === workspaceId)
  if (!workspace) return

  const tourCount = workspace.tours.length
  const photoCount = workspace.photoCount ?? 0
  const dateRange = formatWorkspacePopupDateRange(workspace.activityStart, workspace.activityEnd)
  const html = buildWorkspaceCollectionHoverPopupHtml({
    name: workspace.name,
    ownerNames: workspace.ownerNames,
    isOwner: workspace.isOwner,
    dateRange,
    coverPhotoThumbnailUrl: workspace.coverPhotoThumbnailUrl ?? null,
    tourCount,
    poiCount: 'poiCount' in workspace ? (workspace.poiCount as number | null | undefined) : null,
    photoCount,
  })

  hoverPopup.value = showMapHoverPopup({
    map,
    popup: hoverPopup.value,
    lngLat,
    html,
  })
}

const hideWorkspacePopup = (immediate = false) => {
  if (hidePopupTimer !== null) {
    clearTimeout(hidePopupTimer)
    hidePopupTimer = null
  }
  if (immediate) {
    hoverPopup.value = hideMapHoverPopup(hoverPopup.value)
    return
  }
  hidePopupTimer = setTimeout(() => {
    hoverPopup.value = hideMapHoverPopup(hoverPopup.value)
    hidePopupTimer = null
  }, 300)
}

// ── Track resolution composable ───────────────────────────────────────────────
const { targetResolution, fetchWorkspaceGeometries, scheduleUpdate } = useTrackResolution(
  mapInstance,
  mapLoaded,
)

// ── Per-workspace resolution tracking ────────────────────────────────────────
const renderedResolution = new Map<string, Resolution>()

// ── In-memory feature cache (workspaceId → GeoJSON features) ─────────────────
const mainFeatureCache = new Map<string, GeoJSON.Feature[]>()
let layersInitialized = false

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

// ── Computed visible workspaces ───────────────────────────────────────────────
const effectiveVisibleWorkspaces = computed(() => {
  if (uiStore.isInWorkspacesOverview) {
    return searchWorkspaceStore.visibleWorkspaces
  }
  return []
})

// ── Feature builder ───────────────────────────────────────────────────────────
// Encodes workspaceId and color as GeoJSON feature properties so a single
// shared source+layer pair can render all workspaces without per-workspace layers.
const buildWorkspaceFeatures = (
  workspace: SelectedSearchWorkspace,
  overrideCoords?: [number, number][][],
): GeoJSON.Feature[] => {
  const coordinates = overrideCoords ?? workspace.pointsLineReduced500m
  return coordinates
    .map((coords, index) => {
      if (coords.length < 2) {
        logService.warn(
          LOG_CATEGORIES.MAP,
          `LineString for workspace ${workspace.name} has insufficient coordinates`,
          { workspaceName: workspace.name, coordinatesLength: coords.length },
        )
        return null
      }
      return {
        type: 'Feature' as const,
        properties: {
          workspaceId: workspace.clientId,
          workspaceName: workspace.name,
          color: workspace.color,
          trackIndex: index,
        },
        geometry: {
          type: 'LineString' as const,
          coordinates: coords as number[][],
        },
      }
    })
    .filter((f): f is NonNullable<typeof f> => f !== null)
}

// ── Source builders ───────────────────────────────────────────────────────────
const buildMainFC = (): GeoJSON.FeatureCollection => ({
  type: 'FeatureCollection',
  features: uiStore.areMapElementsForceHidden ? [] : Array.from(mainFeatureCache.values()).flat(),
})

const buildOverlayFC = (): GeoJSON.FeatureCollection => {
  if (uiStore.areMapElementsForceHidden) return EMPTY_FC

  const isBatchSelectionSurface = mobileBatchSelectionStore.activeSurface === 'collections-overview'
  const hoveredId = isBatchSelectionSurface ? null : searchWorkspaceStore.hoveredWorkspaceId
  const selectedIds = isBatchSelectionSurface
    ? mobileBatchSelectionStore.collections
    : new Set<string>()
  const features: GeoJSON.Feature[] = []

  for (const workspaceId of selectedIds) {
    if (workspaceId === hoveredId) continue
    features.push(
      ...(mainFeatureCache.get(workspaceId) ?? []).map((feature) => ({
        ...feature,
        properties: { ...feature.properties, role: 'selected' },
      })),
    )
  }

  if (hoveredId) {
    features.push(
      ...(mainFeatureCache.get(hoveredId) ?? []).map((feature) => ({
        ...feature,
        properties: { ...feature.properties, role: 'hovered' },
      })),
    )
  }

  if (features.length === 0) return EMPTY_FC
  return { type: 'FeatureCollection', features }
}

const refreshMainSource = () => {
  const source = mapInstance.value?.getSource(MAIN_SOURCE_ID) as GeoJSONSource | undefined
  if (source) source.setData(buildMainFC())
}

const refreshOverlaySource = () => {
  const source = mapInstance.value?.getSource(OVERLAY_SOURCE_ID) as GeoJSONSource | undefined
  if (source) source.setData(buildOverlayFC())
}

// ── Cache sync ────────────────────────────────────────────────────────────────
// Keeps mainFeatureCache in sync with effectiveVisibleWorkspaces.
// Higher-res geometry entries are owned by updateSourceResolution and left untouched here.
const syncMainCache = () => {
  const activeIds = new Set(effectiveVisibleWorkspaces.value.map((w) => w.clientId))

  for (const id of [...mainFeatureCache.keys()]) {
    if (!activeIds.has(id)) {
      mainFeatureCache.delete(id)
      renderedResolution.delete(id)
    }
  }

  for (const workspace of effectiveVisibleWorkspaces.value) {
    if (workspace.pointsLineReduced500m.length === 0) continue
    if (!mainFeatureCache.has(workspace.clientId)) {
      mainFeatureCache.set(workspace.clientId, buildWorkspaceFeatures(workspace))
      renderedResolution.set(workspace.clientId, '500m')
    }
  }
}

// ── Event handlers ────────────────────────────────────────────────────────────
type LayerEventHandlers = {
  click: (e: MapLayerMouseEvent) => void
  contextmenu: (e: MapLayerMouseEvent) => void
  mouseenter: (e: MapLayerMouseEvent) => void
  mousemove: (e: MapLayerMouseEvent) => void
  mouseleave: () => void
  touchstart: (e: MapLayerTouchEvent) => void
  touchend: (e: MapLayerTouchEvent) => void
  touchcancel: () => void
  touchmove: (e: maplibregl.MapTouchEvent) => void
}

let registeredHandlers: LayerEventHandlers | null = null

const activeTouchWorkspaceId = ref<string | null>(null)
let activeTouchStartPoint: Point | null = null
let activeTouchStartClientPoint: { x: number; y: number } | null = null
let activeTouchLongPressTimer: ReturnType<typeof setTimeout> | null = null
let activeTouchOpenedContextMenu = false
let swallowNextTouchClick = false
let swallowNextTouchClickTimer: ReturnType<typeof setTimeout> | null = null

const clearHidePopupTimer = () => {
  if (hidePopupTimer !== null) {
    clearTimeout(hidePopupTimer)
    hidePopupTimer = null
  }
}

const clearActiveTouchCandidate = (options: { clearHover?: boolean } = {}) => {
  if (activeTouchLongPressTimer !== null) {
    clearTimeout(activeTouchLongPressTimer)
    activeTouchLongPressTimer = null
  }
  activeTouchWorkspaceId.value = null
  activeTouchStartPoint = null
  activeTouchStartClientPoint = null
  if (options.clearHover) {
    activeTouchOpenedContextMenu = false
  }
  if (options.clearHover) {
    searchWorkspaceStore.setHoveredWorkspaceId(null)
  }
}

const clearTouchClickSuppression = () => {
  swallowNextTouchClick = false
  if (swallowNextTouchClickTimer !== null) {
    clearTimeout(swallowNextTouchClickTimer)
    swallowNextTouchClickTimer = null
  }
}

const suppressNextTouchClickBriefly = () => {
  clearTouchClickSuppression()
  swallowNextTouchClick = true
  swallowNextTouchClickTimer = setTimeout(() => {
    clearTouchClickSuppression()
  }, TOUCH_SYNTHETIC_CLICK_SUPPRESSION_MS)
}

const isWithinTouchTapTolerance = (startPoint: Point | null, currentPoint: Point | undefined) => {
  if (!startPoint || !currentPoint) return false
  const dx = currentPoint.x - startPoint.x
  const dy = currentPoint.y - startPoint.y
  return dx * dx + dy * dy <= TOUCH_TAP_TOLERANCE_PX * TOUCH_TAP_TOLERANCE_PX
}

const navigateToWorkspace = async (workspaceId: string) => {
  if (ongoingRequests.value.has(workspaceId)) {
    logService.debug(
      LOG_CATEGORIES.MAP,
      `Request already in progress for workspace ${workspaceId}, skipping`,
      { workspaceId },
    )
    return
  }

  ongoingRequests.value.add(workspaceId)
  try {
    uiStore.beginMapNavigation('map')
    await router.push({ name: 'workspace-detail', params: { workspaceId } })
  } catch (error) {
    logService.error(LOG_CATEGORIES.MAP, 'Error navigating to workspace from overview map tap', {
      error,
      workspaceId,
    })
  } finally {
    ongoingRequests.value.delete(workspaceId)
  }
}

const setupEventHandlers = (map: MapLibreMap) => {
  const clickHandler = async (e: MapLayerMouseEvent) => {
    if (isTrackDrawingActive.value) return
    const feature = e.features?.[0]
    const workspaceId = feature?.properties?.workspaceId as string | undefined
    if (!workspaceId) return

    if (isTouchEnvironment && swallowNextTouchClick) {
      clearTouchClickSuppression()
      return
    }

    if (mobileBatchSelectionStore.activeSurface === 'collections-overview') {
      hideWorkspacePopup(true)
      mobileBatchSelectionStore.toggle('collections', workspaceId)
      return
    }

    await navigateToWorkspace(workspaceId)
  }

  const contextMenuHandler = (e: MapLayerMouseEvent) => {
    if (isTrackDrawingActive.value) return
    const feature = e.features?.[0]
    const workspaceId = feature?.properties?.workspaceId as string | undefined
    if (!workspaceId) return
    e.preventDefault()
    searchWorkspaceStore.setHoveredWorkspaceId(workspaceId)
    hideWorkspacePopup(true)
    emit('workspace-context-menu', {
      workspaceId,
      clientX: e.originalEvent.clientX,
      clientY: e.originalEvent.clientY,
    })
  }

  const mouseEnterHandler = (e: MapLayerMouseEvent) => {
    if (isTrackDrawingActive.value) return
    const workspaceId = e.features?.[0]?.properties?.workspaceId as string | undefined
    if (!workspaceId) return
    map.getCanvas().style.cursor = 'pointer'
    if (!isTouchEnvironment) {
      searchWorkspaceStore.setHoveredWorkspaceId(workspaceId)
      showWorkspacePopup(e.lngLat, workspaceId)
    }
  }

  const mouseMoveHandler = (e: MapLayerMouseEvent) => {
    if (isTrackDrawingActive.value || isTouchEnvironment) return
    clearHidePopupTimer()
    if (hoverPopup.value) {
      hoverPopup.value.setLngLat(e.lngLat)
    } else {
      const workspaceId = e.features?.[0]?.properties?.workspaceId as string | undefined
      if (workspaceId) showWorkspacePopup(e.lngLat, workspaceId)
    }
  }

  const mouseLeaveHandler = () => {
    if (isTrackDrawingActive.value) return
    map.getCanvas().style.cursor = ''
    if (!isTouchEnvironment) {
      searchWorkspaceStore.setHoveredWorkspaceId(null)
      hideWorkspacePopup()
    }
  }

  const touchStartHandler = (e: MapLayerTouchEvent) => {
    if (isTrackDrawingActive.value) return
    const workspaceId = e.features?.[0]?.properties?.workspaceId as string | undefined
    if (!workspaceId) return
    const touch = e.originalEvent.changedTouches?.[0] ?? e.originalEvent.touches?.[0]
    activeTouchOpenedContextMenu = false
    activeTouchWorkspaceId.value = workspaceId
    activeTouchStartPoint = e.point
    activeTouchStartClientPoint = touch
      ? { x: touch.clientX, y: touch.clientY }
      : { x: e.point.x, y: e.point.y }
    if (activeTouchLongPressTimer !== null) {
      clearTimeout(activeTouchLongPressTimer)
    }
    activeTouchLongPressTimer = setTimeout(() => {
      if (activeTouchWorkspaceId.value !== workspaceId || !activeTouchStartClientPoint) return
      activeTouchOpenedContextMenu = true
      searchWorkspaceStore.setHoveredWorkspaceId(workspaceId)
      suppressNextTouchClickBriefly()
      emit('workspace-context-menu', {
        workspaceId,
        clientX: activeTouchStartClientPoint.x,
        clientY: activeTouchStartClientPoint.y,
      })
      clearActiveTouchCandidate()
    }, TOUCH_LONG_PRESS_DELAY_MS)
  }

  const touchEndHandler = async (e: MapLayerTouchEvent) => {
    if (isTrackDrawingActive.value) return
    if (activeTouchOpenedContextMenu) {
      activeTouchOpenedContextMenu = false
      clearActiveTouchCandidate()
      return
    }
    const workspaceId = e.features?.[0]?.properties?.workspaceId as string | undefined
    const activeWorkspaceId = activeTouchWorkspaceId.value
    const shouldEnter =
      workspaceId !== undefined &&
      activeWorkspaceId !== null &&
      workspaceId === activeWorkspaceId &&
      isWithinTouchTapTolerance(activeTouchStartPoint, e.point)

    if (shouldEnter) {
      suppressNextTouchClickBriefly()
      clearActiveTouchCandidate()
      if (mobileBatchSelectionStore.activeSurface === 'collections-overview') {
        hideWorkspacePopup(true)
        mobileBatchSelectionStore.toggle('collections', workspaceId)
        return
      }
      await navigateToWorkspace(workspaceId)
      return
    }

    clearActiveTouchCandidate({ clearHover: true })
  }

  const touchCancelHandler = () => {
    clearActiveTouchCandidate({ clearHover: true })
  }

  const touchMoveHandler = (e: maplibregl.MapTouchEvent) => {
    if (activeTouchWorkspaceId.value === null) return
    if (isWithinTouchTapTolerance(activeTouchStartPoint, e.point)) return
    clearActiveTouchCandidate({ clearHover: true })
  }

  registeredHandlers = {
    click: clickHandler,
    contextmenu: contextMenuHandler,
    mouseenter: mouseEnterHandler,
    mousemove: mouseMoveHandler,
    mouseleave: mouseLeaveHandler,
    touchstart: touchStartHandler,
    touchend: touchEndHandler,
    touchcancel: touchCancelHandler,
    touchmove: touchMoveHandler,
  }
  map.on('click', MAIN_TOUCH_LAYER_ID, clickHandler)
  map.on('contextmenu', MAIN_TOUCH_LAYER_ID, contextMenuHandler)
  map.on('mouseenter', MAIN_TOUCH_LAYER_ID, mouseEnterHandler)
  map.on('mousemove', MAIN_TOUCH_LAYER_ID, mouseMoveHandler)
  map.on('mouseleave', MAIN_TOUCH_LAYER_ID, mouseLeaveHandler)
  map.on('touchstart', MAIN_TOUCH_LAYER_ID, touchStartHandler)
  map.on('touchend', MAIN_TOUCH_LAYER_ID, touchEndHandler)
  map.on('touchcancel', MAIN_TOUCH_LAYER_ID, touchCancelHandler)
  map.on('touchmove', touchMoveHandler)
}

const removeEventHandlers = (map: MapLibreMap) => {
  if (!registeredHandlers) return
  map.off('click', MAIN_TOUCH_LAYER_ID, registeredHandlers.click)
  map.off('contextmenu', MAIN_TOUCH_LAYER_ID, registeredHandlers.contextmenu)
  map.off('mouseenter', MAIN_TOUCH_LAYER_ID, registeredHandlers.mouseenter)
  map.off('mousemove', MAIN_TOUCH_LAYER_ID, registeredHandlers.mousemove)
  map.off('mouseleave', MAIN_TOUCH_LAYER_ID, registeredHandlers.mouseleave)
  map.off('touchstart', MAIN_TOUCH_LAYER_ID, registeredHandlers.touchstart)
  map.off('touchend', MAIN_TOUCH_LAYER_ID, registeredHandlers.touchend)
  map.off('touchcancel', MAIN_TOUCH_LAYER_ID, registeredHandlers.touchcancel)
  map.off('touchmove', registeredHandlers.touchmove)
  registeredHandlers = null
  clearTouchClickSuppression()
  clearActiveTouchCandidate({ clearHover: true })
}

// ── Layer initialization / teardown ──────────────────────────────────────────
const initializeLayers = () => {
  const map = mapInstance.value
  if (!map) return
  const firstSymbolLayerId = findFirstSymbolLayerId(map)

  map.addSource(MAIN_SOURCE_ID, { type: 'geojson', data: buildMainFC() })
  map.addSource(OVERLAY_SOURCE_ID, { type: 'geojson', data: buildOverlayFC() })

  // Main outline — white halo behind the colored line
  map.addLayer(
    {
      id: MAIN_OUTLINE_LAYER_ID,
      type: 'line',
      source: MAIN_SOURCE_ID,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': '#ffffff',
        'line-width': DEFAULT_OUTLINE_WIDTH,
        'line-opacity': DEFAULT_OUTLINE_OPACITY,
      },
    },
    firstSymbolLayerId,
  )

  // Main line — color driven by feature property
  map.addLayer(
    {
      id: MAIN_LINE_LAYER_ID,
      type: 'line',
      source: MAIN_SOURCE_ID,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': DEFAULT_LINE_WIDTH,
      },
    },
    firstSymbolLayerId,
  )

  // Overlay outline — wider white halo for the hovered collection
  map.addLayer(
    {
      id: OVERLAY_OUTLINE_LAYER_ID,
      type: 'line',
      source: OVERLAY_SOURCE_ID,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': '#ffffff',
        'line-width': HOVER_OUTLINE_WIDTH,
        'line-opacity': HOVER_OUTLINE_OPACITY,
      },
    },
    firstSymbolLayerId,
  )

  // Overlay line — thicker colored line for the hovered collection
  map.addLayer(
    {
      id: OVERLAY_LINE_LAYER_ID,
      type: 'line',
      source: OVERLAY_SOURCE_ID,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': HOVER_LINE_WIDTH,
      },
    },
    firstSymbolLayerId,
  )

  // Touch target — invisible wide layer on top, receives all pointer events
  map.addLayer(
    {
      id: MAIN_TOUCH_LAYER_ID,
      type: 'line',
      source: MAIN_SOURCE_ID,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': 'transparent', 'line-width': 20, 'line-opacity': 0 },
    },
    firstSymbolLayerId,
  )

  setupEventHandlers(map)
  layersInitialized = true
}

const removeLayers = () => {
  const map = mapInstance.value
  if (!map) return

  removeEventHandlers(map)

  for (const layerId of [
    MAIN_TOUCH_LAYER_ID,
    OVERLAY_LINE_LAYER_ID,
    OVERLAY_OUTLINE_LAYER_ID,
    MAIN_LINE_LAYER_ID,
    MAIN_OUTLINE_LAYER_ID,
  ]) {
    try {
      if (map.getLayer(layerId)) map.removeLayer(layerId)
    } catch (error) {
      logService.warn(LOG_CATEGORIES.MAP, 'Error removing search workspace layer', {
        layerId,
        error,
      })
    }
  }

  for (const sourceId of [MAIN_SOURCE_ID, OVERLAY_SOURCE_ID]) {
    try {
      if (map.getSource(sourceId)) map.removeSource(sourceId)
    } catch (error) {
      logService.warn(LOG_CATEGORIES.MAP, 'Error removing search workspace source', {
        sourceId,
        error,
      })
    }
  }

  layersInitialized = false
}

// ── Main update ───────────────────────────────────────────────────────────────
const updateLayers = (forceRebuild = false) => {
  if (!mapInstance.value || !mapLoaded.value) return

  if (forceRebuild) {
    // Map style was reloaded — native layers/sources are already gone; reset all tracking
    layersInitialized = false
    mainFeatureCache.clear()
    renderedResolution.clear()
  }

  syncMainCache()

  if (!layersInitialized) {
    initializeLayers()
  } else {
    refreshMainSource()
    refreshOverlaySource()
  }
}

// ── Resolution update ─────────────────────────────────────────────────────────
const updateSourceResolution = async (signal: AbortSignal) => {
  const res = targetResolution.value
  if (!mapInstance.value) return

  if (res === '500m') {
    let changed = false
    for (const workspace of effectiveVisibleWorkspaces.value) {
      if (renderedResolution.get(workspace.clientId) === '500m') continue
      mainFeatureCache.set(workspace.clientId, buildWorkspaceFeatures(workspace))
      renderedResolution.set(workspace.clientId, '500m')
      changed = true
    }
    if (changed) {
      refreshMainSource()
      refreshOverlaySource()
    }
    return
  }

  const visibleIds = effectiveVisibleWorkspaces.value.map((w) => w.clientId)
  if (visibleIds.length === 0) return

  const geometries = await fetchWorkspaceGeometries(visibleIds, res, signal)

  let changed = false
  for (const [clientId, coords] of geometries) {
    if (renderedResolution.get(clientId) === res) continue
    const workspace = effectiveVisibleWorkspaces.value.find((w) => w.clientId === clientId)
    if (!workspace) continue
    mainFeatureCache.set(clientId, buildWorkspaceFeatures(workspace, coords))
    renderedResolution.set(clientId, res)
    changed = true
  }

  if (changed) {
    refreshMainSource()
    refreshOverlaySource()
  }
}

const handleMoveEnd = () => {
  if (uiStore.areMapElementsForceHidden) return
  if (targetResolution.value !== '500m') {
    scheduleUpdate(updateSourceResolution)
  }
}

// ── Watchers ──────────────────────────────────────────────────────────────────
watch(
  () => effectiveVisibleWorkspaces.value,
  () => updateLayers(),
  {
    deep: true,
    immediate: true,
  },
)

watch(
  [mapInstance, mapLoaded],
  ([map, loaded], previousValues) => {
    const previousMap = previousValues?.[0] ?? null
    previousMap?.off('moveend', handleMoveEnd)
    if (!loaded) return
    updateLayers()
    map?.on('moveend', handleMoveEnd)
  },
  { immediate: true },
)

watch(
  () => mapStyleRevision.value,
  () => {
    if (mapLoaded.value) updateLayers(true)
  },
)

watch(targetResolution, () => {
  if (uiStore.areMapElementsForceHidden) return
  scheduleUpdate(updateSourceResolution)
})

// Hover change → rebuild overlay source (replaces per-layer setPaintProperty calls)
watch(
  () => searchWorkspaceStore.hoveredWorkspaceId,
  () => {
    if (!mapLoaded.value) return
    refreshOverlaySource()
  },
)

watch(
  () => [
    mobileBatchSelectionStore.activeSurface,
    [...mobileBatchSelectionStore.collections].sort().join('|'),
  ],
  () => {
    if (!mapLoaded.value) return
    refreshOverlaySource()
  },
)

// Hold the M key (or toggle the hide button) to hide all search-result tracks,
// matching WorkspaceTourLayer/LibraryTourLayer. Refresh sources on toggle; on
// un-hide, re-run a resolution upgrade in case the viewport changed while hidden.
let forceHideStartResolution: Resolution | null = null
watch(
  () => uiStore.areMapElementsForceHidden,
  (isHidden) => {
    if (!mapLoaded.value) return
    if (isHidden) {
      forceHideStartResolution = targetResolution.value
      refreshMainSource()
      refreshOverlaySource()
      return
    }

    const shouldRefreshResolution =
      forceHideStartResolution !== null && targetResolution.value !== forceHideStartResolution
    forceHideStartResolution = null

    refreshMainSource()
    refreshOverlaySource()
    if (shouldRefreshResolution && targetResolution.value !== '500m') {
      scheduleUpdate(updateSourceResolution)
    }
  },
)

// ── Cleanup ───────────────────────────────────────────────────────────────────
onUnmounted(() => {
  ongoingRequests.value.clear()
  clearTouchClickSuppression()

  if (searchWorkspaceStore.hoveredWorkspaceId !== null) {
    searchWorkspaceStore.setHoveredWorkspaceId(null)
  }

  hideWorkspacePopup(true)

  if (!mapInstance.value) return

  mapInstance.value.off('moveend', handleMoveEnd)
  removeLayers()
  mainFeatureCache.clear()
})
</script>

<template>
  <div></div>
</template>
